import { FC } from 'react';

import { FaCheckCircle } from 'react-icons/fa';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { BiCopy } from 'react-icons/bi';
import cn from 'classnames';
import {
  CalendarCheckIcon,
  DescriptionIcon,
  VideoIcon,
} from 'ui/components/icon';
import { Button, Tooltip } from 'ui/components/common';
import {
  addClassNameToRichText,
  copyToClipboard,
  sanitizeRichTextHtml,
} from 'utils/helpers';

import { BOOKING_DETAILS } from './ClientBooking';
import { CLIENT_BOOKING_FORM } from '../../forms/schema';

type PROPS = {
  bookingDetails: BOOKING_DETAILS;
};

const ClientBookingSuccess: FC<PROPS> = ({ bookingDetails }) => {
  const { values } = useFormikContext<CLIENT_BOOKING_FORM>();

  const startAt = dayjs(bookingDetails.startAt).format('h:mmA');
  const endAt = dayjs(bookingDetails.endAt).format('h:mmA');

  const convertedTimezone = values.startAt?.convertedTimezone || '';
  const bookingDetailsDuration = bookingDetails.duration || 0;
  return (
    <div className="shadow-leapify-sm max-w-[700px] rounded-[6px] bg-fill-tertiary p-8">
      <div
        className={cn(
          'mb-[30px] space-y-2 pb-[18px]',
          'flex flex-col items-center',
          'border-b-[0.5px] border-b-separator-primary',
        )}
      >
        <FaCheckCircle className="h-[50px] w-[50px] text-primary-base" />
        <div className="text-[22px] font-bold leading-7 tracking-[0.88px]">
          CONFIRMED
        </div>
      </div>
      <div>
        <div className="pb-3">
          <div className="text-lg font-bold leading-[22px] tracking-[1px]">
            {bookingDetails.title}
          </div>
          <p className="font-semibold text-xs">
            {`${dayjs
              .tz(values.selectedDate, convertedTimezone)
              .format(
                'dddd MMMM D, YYYY',
              )} | ${startAt} - ${endAt} (${convertedTimezone}) | 
              ${bookingDetailsDuration} Minutes`}
          </p>
        </div>
        <div className="flex items-center gap-4 py-3">
          <CalendarCheckIcon className="h-5 w-5 flex-shrink-0 text-content" />
          <p className="text-sm font-semibold">
            {`${values?.firstName} ${values?.lastName ?? ''}`.trim()}
          </p>
        </div>
        <div className="flex items-center gap-4 py-3">
          <VideoIcon className="h-5 w-5 flex-shrink-0 text-content" />
          <div className="flex w-full justify-between gap-2">
            <Button
              intent={'secondary'}
              className="bg-secondary-dark"
              size={'sm'}
              label="JOIN THE MEETING"
              onClick={() => window.open(bookingDetails?.eventUrl, '_blank')}
            />
            <div
              className={cn(
                'inline-flex items-center gap-2',
                'text-xs font-semibold leading-4 tracking-wider text-content',
              )}
            >
              <div className="break-all">{bookingDetails?.eventUrl ?? ''}</div>
              <Tooltip title="" description="Copy to clipboard">
                <BiCopy
                  type="button"
                  onClick={() => copyToClipboard(bookingDetails.eventUrl)}
                  className="h-5 w-5 text-content"
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="flex gap-4 py-3 items-start">
          <DescriptionIcon className="h-5 w-5 flex-shrink-0 text-content" />
          <div
            className="break-all"
            dangerouslySetInnerHTML={{
              __html: sanitizeRichTextHtml(
                addClassNameToRichText(
                  'text-sm',
                  bookingDetails?.content ?? '<p>None</p>',
                ) as string,
              ),
            }}
          />
        </div>
      </div>
      <div
        className={cn(
          'mt-4 pt-[18px]',
          'flex justify-center',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          type="button"
          size={'md'}
          onClick={() => {
            // eslint-disable-next-line max-len
            window.location.href = `${import.meta.env.VITE_APP_END_POINT}/api/public/calendar/events/${bookingDetails.eventId}/download`;
          }}
          label="DOWNLOAD ICS"
        />
      </div>
    </div>
  );
};

export default ClientBookingSuccess;
